
import * as React from "react"
import { AlertCircle } from "lucide-react"
import { Button } from "./button"
import { Alert, AlertDescription, AlertTitle } from "./alert"

interface ErrorBoundaryProps {
  children: React.ReactNode
  fallback?: React.ReactNode
  onReset?: () => void
  componentName?: string
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error(`Error in ${this.props.componentName || 'component'}:`, error, errorInfo)
  }

  handleReset = (): void => {
    this.setState({ hasError: false, error: null })
    if (this.props.onReset) {
      this.props.onReset()
    }
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      // Check for database-related errors
      const isDatabaseError = this.state.error?.message?.includes('relation') && 
                             this.state.error?.message?.includes('does not exist');
      
      return (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>
            {isDatabaseError ? "Database Error" : "Something went wrong"}
          </AlertTitle>
          <AlertDescription className="flex flex-col gap-2">
            <div>
              {isDatabaseError
                ? `The ${this.props.componentName || "component"} is trying to access database tables that don't exist. This feature may be deprecated.`
                : this.props.componentName 
                  ? `Failed to load ${this.props.componentName}` 
                  : "An error occurred while rendering this component"}
            </div>
            {this.state.error && (
              <div className="text-xs opacity-80">
                {this.state.error.message}
              </div>
            )}
            <Button
              variant="outline"
              size="sm" 
              onClick={this.handleReset}
              className="mt-2 self-start"
            >
              Try again
            </Button>
          </AlertDescription>
        </Alert>
      )
    }

    return this.props.children
  }
}
